import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  Tooltip
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import { getNotificationList, getResourceList } from "api/layer";
import { AppContext } from "helpers/context";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteMessageModal from "../delete-message-modal/delete-message-modal";
import MessageEditModal from "../message-edit-modal/message-edit-modal";
import SearchInput from "../../tenant/search-input/search-input";
import styles from "./messages-manage.module.scss";
import { MessageType } from "../type";

type SortableField =
  | "notification_type"
  | "notification_message"
  | "start_time"
  | "expiration_time"
  | "no_expiration_time"
  | "is_banner"
  | "is_ring"
  | "banner_publish_time"
  | "ring_publish_time"
  | "release_version"
  | "status"
  | "create_time";

function MessageManage() {
  const [context, dispatch] = useContext(AppContext);
  const [t] = useTranslation();
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [currentMessage, setCurrentMessage] = useState<MessageType | null>(
    null
  );
  const [tableData, setTableData] = useState<MessageType[]>([]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openDeleteMessageModal, setOpenDeleteMessageModal] =
    useState<boolean>(false);
  const [releaseVersions, setReleaseVersions] = useState<
    { id: number; version: string }[]
  >([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortByField, setSortByField] = useState<SortableField>("create_time");
  const [sortOrder, setSortOrder] = useState<
    Record<SortableField, "asc" | "desc">
  >({
    notification_type: "asc",
    notification_message: "asc",
    start_time: "desc",
    expiration_time: "asc",
    no_expiration_time: "asc",
    is_banner: "asc",
    is_ring: "asc",
    banner_publish_time: "desc",
    ring_publish_time: "desc",
    release_version: "asc",
    status: "asc",
    create_time: "desc",
  });

  const fetchReleaseVersions = async () => {
    try {
      const res: any = await getResourceList();
      const details = res?.data?.releaseDetails || res?.releaseDetails || [];
      if (Array.isArray(details)) {
        const versions = details
          .filter((item: any) => item.resourceType === "Release Info")
          .map((item: any) => ({
            id: item.resourceId,
            version: item.resourceVersion ?? "",
          }))
          .filter((v: { id: number; version: string }) => v.version);
        setReleaseVersions(versions);
      }
    } catch (error) {
      console.error("Failed to fetch release versions:", error);
    }
  };

  const initData = async () => {
    setIsLoading(true);

    const param: any = {
      keyword: searchKeyword,
    };

    const pageParam: any = {
      pageNo: page + 1,
      pageSize: rowsPerPage,
      sortOrderAsc: sortOrder[sortByField] === "asc",
      sortByField: sortByField,
    };

    getNotificationList(param, pageParam)
      .then((res: any) => {
        if (res?.code !== 200 || !res.data?.rows) {
          return;
        }

        setTableData(res.data.rows);
        setTotalCount(res.data.totalCount * 1 || 0);
      })
      .finally(() => setIsLoading(false));
  };

  const handleDelete = (message: any) => {
    setCurrentMessage(message);
    setOpenDeleteMessageModal(true);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return dateString.split(" ")[0];
  };

  const handleSave = () => {
    setOpen(false);
    initData();
  };

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
    setPage(0);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: SortableField) => {
    setSortByField(field);
    setSortOrder((prev) => ({
      ...prev,
      [field]: prev[field] === "asc" ? "desc" : "asc",
    }));
    setPage(0);
  };

  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const clearFilters = () => {
    setSearchKeyword("");
    setShowFilters(false);
    setPage(0);
  };

  const handleGetBodyTable = () => {
    if (isLoading) {
      return <TableRow>
        <TableCell colSpan={12}>
          <div className={styles.loading}>
            <div className="loading loading-spinner loading-lg"></div>
            <div>{t("layers.loading")}</div>
          </div>
        </TableCell>
      </TableRow>
    } else if (tableData.length === 0) {
      return <TableRow>
        <TableCell colSpan={12} style={{ textAlign: "center" }}>
          {t("releases.noData")}
        </TableCell>
      </TableRow>
    } else {
      return tableData.map((message: any) => (
        <TableRow
          key={message.notificationId}
          hover
          onMouseEnter={() => setHoveredRow(message.notificationId)}
          onMouseLeave={() => setHoveredRow(null)}
          className={styles.row}
        >
          <TableCell>
            {(() => {
              let notificationTypeLabel = message.notificationType;
              if (message.notificationType === "System notification") {
                notificationTypeLabel = t("userSystem.notificationTypeSystemNotification");
              } else if (message.notificationType === "Release") {
                notificationTypeLabel = t("userSystem.notificationTypeRelease");
              } else if (message.notificationType === "Welcome") {
                notificationTypeLabel = t("userSystem.notificationTypeWelcome");
              }
              return notificationTypeLabel;
            })()}
          </TableCell>
          <TableCell title={message.notificationMessage}>
            {message.notificationMessage}
          </TableCell>
          <TableCell>
            {message.status === "publish"
              ? t("userSystem.statusPublish")
              : t("userSystem.statusDraft")}
          </TableCell>
          <TableCell>
            {message.isBanner
              ? t("userSystem.yes")
              : t("userSystem.no")}
          </TableCell>
          <TableCell>
            {message.isRing ? t("userSystem.yes") : t("userSystem.no")}
          </TableCell>
          <TableCell>
            {message.noExpirationTime
              ? t("userSystem.yes")
              : t("userSystem.no")}
          </TableCell>
          <TableCell>{formatDate(message.startTime)}</TableCell>
          <TableCell>{formatDate(message.expirationTime)}</TableCell>
          <TableCell>{formatDate(message.bannerPublishTime)}</TableCell>
          <TableCell>{formatDate(message.ringPublishTime)}</TableCell>
          <TableCell>
            {message.notificationType === "Release"
              ? message.releaseVersion
              : ""}
          </TableCell>
          {context.menu?.find((item) => item.id === "100002") && (
            <TableCell>
              {hoveredRow === message.notificationId && (
                <div className={styles.delete}>
                  <div
                    className={`${styles.icons} delete-set`}
                    onClick={(evt) => evt.stopPropagation()}
                  >
                    <Tooltip title={t("tooltip.edit")}>
                      <IconButton
                        onClick={() => {
                          setOpen(true);
                          setCurrentMessage(message);
                        }}
                      >
                        <span className="material-icons">edit</span>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("tooltip.delete")}>
                      <IconButton
                        onClick={() => handleDelete(message)}
                      >
                        <span className="material-icons">delete</span>
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              )}
            </TableCell>
          )}
        </TableRow>
      ))
    }
  }

  useEffect(() => {
    fetchReleaseVersions();
  }, []);

  useEffect(() => {
    initData();
  }, [page, rowsPerPage, searchKeyword, sortByField, sortOrder]);

  return (
    <div className={styles.container}>
      <Toolbar
        style={{
          justifyContent: "space-between",
          borderBottom: "1px solid #ccc",
          marginBottom: "5px",
          padding: "0",
        }}
      >
        {context.menu?.find((item) => item.id === "100002") && (
          <Button
            variant="contained"
            color="success"
            size="small"
            endIcon={<span className="material-icons">add</span>}
            onClick={() => {
              setOpen(true);
              setCurrentMessage(null);
            }}
          >
            {t("userSystem.addMessage")}
          </Button>
        )}
        <IconButton
          color="success"
          onClick={toggleFilters}
          style={{
            color: showFilters ? "#2e7d32" : "rgba(0, 0, 0, 0.54)",
            background: showFilters ? "#c1e5ca" : "",
          }}
        >
          <span className="material-icons">filter_list</span>
        </IconButton>
      </Toolbar>

      {showFilters && (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px",
            backgroundColor: "#f5f5f5",
            justifyContent: "space-between",
          }}
        >
          <Box style={{ display: "flex", flex: 1, alignItems: "center" }}>
            <SearchInput onSearch={handleSearch} />
          </Box>
          <IconButton onClick={clearFilters} aria-label="clear" color="default">
            <span className="material-icons">clear</span>
          </IconButton>
        </Box>
      )}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Table
          stickyHeader
          aria-label="sticky table"
          size="small"
          className={styles.table}
        >
          <TableHead>
            <TableRow>
              <TableCell style={{ width: "100px" }}>
                <TableSortLabel
                  active={sortByField === "notification_type"}
                  onClick={() => handleSort("notification_type")}
                  direction={sortOrder.notification_type}
                >
                  {t("userSystem.messageType")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ minWidth: "300px" }}>
                {t("userSystem.messageContent")}
              </TableCell>
              <TableCell style={{ width: "100px" }}>
                <TableSortLabel
                  active={sortByField === "status"}
                  onClick={() => handleSort("status")}
                  direction={sortOrder["status"]}
                >
                  {t("userSystem.status")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "100px" }}>
                <TableSortLabel
                  active={sortByField === "is_banner"}
                  onClick={() => handleSort("is_banner")}
                  direction={sortOrder.is_banner}
                >
                  {t("userSystem.isBannerMessage")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "100px" }}>
                <TableSortLabel
                  active={sortByField === "is_ring"}
                  onClick={() => handleSort("is_ring")}
                  direction={sortOrder.is_ring}
                >
                  {t("userSystem.isRingMessage")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "110px" }}>
                <TableSortLabel
                  active={sortByField === "no_expiration_time"}
                  onClick={() => handleSort("no_expiration_time")}
                  direction={sortOrder.no_expiration_time}
                >
                  {t("userSystem.hasUnlimitedTime")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "130px" }}>
                <TableSortLabel
                  active={sortByField === "start_time"}
                  onClick={() => handleSort("start_time")}
                  direction={sortOrder.start_time}
                >
                  {t("userSystem.messageStartDate")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "130px" }}>
                <TableSortLabel
                  active={sortByField === "expiration_time"}
                  onClick={() => handleSort("expiration_time")}
                  direction={sortOrder.expiration_time}
                >
                  {t("userSystem.messageExpireDate")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "120px" }}>
                <TableSortLabel
                  active={sortByField === "banner_publish_time"}
                  onClick={() => handleSort("banner_publish_time")}
                  direction={sortOrder.banner_publish_time}
                >
                  {t("userSystem.bannerPublishTime")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "120px" }}>
                <TableSortLabel
                  active={sortByField === "ring_publish_time"}
                  onClick={() => handleSort("ring_publish_time")}
                  direction={sortOrder.ring_publish_time}
                >
                  {t("userSystem.ringPublishTime")}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "80px" }}>
                {t("userSystem.note")}
              </TableCell>
              {context.menu?.find((item) => item.id === "100002") && (
                <TableCell style={{ width: "120px" }}></TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {handleGetBodyTable()}
          </TableBody>
        </Table>
      </div>
      <div style={{ marginLeft: "auto", flexShrink: 0 }}>
        <TablePagination
          component="div"
          count={totalCount}
          rowsPerPageOptions={[10, 15, 25]}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t("userSystem.rowsPerPage")}
          slotProps={{
            select: {
              native: true,
            },
          }}
        />
      </div>
      {openDeleteMessageModal && currentMessage && (
        <DeleteMessageModal
          data={currentMessage}
          initData={initData}
          onClose={() => setOpenDeleteMessageModal(false)}
        />
      )}
      {open && (
        <MessageEditModal
          open={open}
          onClose={() => setOpen(false)}
          onSave={handleSave}
          data={currentMessage || undefined}
          releaseVersions={releaseVersions}
        />
      )}
    </div>
  );
}

export default MessageManage;
